import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { contractConfigApi, type PingPayout } from '../services/api';
import { PingPayoutStatus } from './PingPayoutStatus';

export function PingPayoutAmounts({ payout }: { payout: PingPayout }) {
  const { t, i18n } = useTranslation();
  const money = (ore: number) => new Intl.NumberFormat(i18n.language, { style: 'currency', currency: payout.currency }).format(ore / 100);
  return <dl className="row g-2 mb-0 text-break">
    <dt className="col-12 col-sm-7">{t('pingPayout.amount')}</dt><dd className="col-12 col-sm-5">{money(payout.amountOre)}</dd>
    <dt className="col-12 col-sm-7">{t('pingPayout.fee')}</dt><dd className="col-12 col-sm-5">{money(payout.feeOre)}</dd>
    <dt className="col-12 col-sm-7">{t('pingPayout.companyAmount')}</dt><dd className="col-12 col-sm-5">{money(payout.companyAmountOre)}</dd>
  </dl>;
}

export function PingPayoutPanel({ projectId, refreshToken, canUseDebugBank, embedded = false, onPayoutsChanged }: { projectId: string; refreshToken: number; canUseDebugBank: boolean; embedded?: boolean; onPayoutsChanged?: (payouts: PingPayout[]) => void }) {
  const { t } = useTranslation();
  const [payouts, setPayouts] = useState<PingPayout[]>([]);
  const [error, setError] = useState(false);
  useEffect(() => {
    let active = true;
    let timer: ReturnType<typeof setTimeout>;
    setPayouts([]); setError(false);
    onPayoutsChanged?.([]);
    const load = async () => {
      try {
        const values = await contractConfigApi.getPingPayouts(projectId);
        if (!active) return;
        setPayouts(values); setError(false);
        onPayoutsChanged?.(values);
        if (values.some(p => !['confirmed', 'failed', 'returned'].includes(p.state))) timer = setTimeout(() => void load(), 10000);
      } catch { if (active) setError(true); }
    };
    void load();
    return () => { active = false; clearTimeout(timer); };
  }, [projectId, refreshToken, onPayoutsChanged]);
  if (!payouts.length) return error ? <p className="alert alert-warning" role="status">{t('pingPayout.loadError')}</p> : null;
  return <section className={embedded ? 'mb-4' : 'card mb-4'} aria-labelledby="ping-payout-heading">
    <div className={embedded ? undefined : 'card-body'}>
      <h2 id="ping-payout-heading" className="h5">{t('pingPayout.title')}</h2>
      <p>{t('pingPayout.feeExplanation')}</p>
      {error && <p className="alert alert-warning" role="status">{t('pingPayout.loadError')}</p>}
      <div className="row g-3">
        {payouts.map(payout => <div className={embedded ? 'col-12' : 'col-12 col-lg-6'} key={payout.id}>
          <article className="border rounded p-3 h-100 text-break">
            <h3 className="h6">{t('pingPayout.stage', { contract: payout.contractNumber, milestone: payout.milestoneNumber })}</h3>
            <PingPayoutStatus payout={payout} canUseDebugBank={canUseDebugBank} />
            <PingPayoutAmounts payout={payout} />
            {payout.companyAmountOre === 0 && <p className="small mb-0">{t('pingPayout.feeOnly')}</p>}
            {payout.mode === 'sandbox' && (payout.debugBankAccessible ?? (canUseDebugBank && payout.debugBankAvailable)) && <Link className="btn btn-outline-primary btn-lg w-100 mt-3" to={`/debug-bank/payout/${encodeURIComponent(payout.id)}`}>{t('pingPayout.openBank')}</Link>}
          </article>
        </div>)}
      </div>
    </div>
  </section>;
}
